/**
 * Mojo Up AI Cloud Teams billing (docs/plans/2026-10-mojo-up-cloud.md, phase 4).
 *
 * The customer portal is the only billing system and Cloud never calls Stripe. Cloud asks the
 * portal for Checkout, the Stripe customer portal and seat changes (server to server, with a
 * shared key), and learns what a team has paid for from an Ed25519-signed entitlement token in
 * the portal's `saa1.` format. The portal tells Cloud that something changed with an ids-only,
 * HMAC-signed push; Cloud then pulls the token. A forged or replayed push therefore does nothing.
 *
 * The portal half lives in mju-mojoupcustomerportal (`packages/api/src/routes/integrations/cloud.ts`).
 * Change both sides together.
 */
import { z } from "zod";
import { cloudPlanSchema } from "./cloud.js";
/** The product slug in the token's `prd` claim and the portal's product row. */
export const CLOUD_PRODUCT_SLUG = "mojo-up-cloud";
/**
 * How many people a team on the free plan holds: its owner and nobody else. Invitations need a
 * trial or Teams. A product rule JP may change; this is the one place it lives.
 */
export const CLOUD_FREE_TEAM_SEATS = 1;
/** A$50 per person per month, excluding GST (Stripe Tax adds 10%). Display only; Stripe holds the price. */
export const CLOUD_TEAMS_PRICE = { amountCents: 5000, currency: "AUD", interval: "month" };
export const CLOUD_TEAMS_PRICE_TEXT = "A$50 per person per month, plus GST";
/** Days of free trial a team gets once, without a card. */
export const CLOUD_TRIAL_DAYS = 14;
/** The most seats one team can buy online. */
export const CLOUD_MAX_SEATS = 500;
/** Stripe's subscription status, or `none` when the team has never subscribed. */
export const cloudSubscriptionStatusSchema = z.enum(["trialing", "active", "past_due", "paused", "unpaid", "canceled", "incomplete", "incomplete_expired", "none"]);
/** The statuses that give a team Teams access; `past_due` with a banner while Stripe retries. */
export const TEAMS_ACCESS_STATUSES = new Set(["trialing", "active", "past_due"]);
// ── Redeemable licences (docs/plans/2026-10-cloud-licences-and-operations.md) ──────────────────
/**
 * Where a team's Teams entitlement comes from: a Stripe subscription, or a licence Mojo Up issued in
 * the customer portal and the team's owner redeemed (single use, revocable, no subscription). A
 * valid licence takes precedence over a subscription; the portal signs whichever applies.
 */
export const cloudEntitlementSourceSchema = z.enum(["subscription", "licence"]);
/** The licence behind a licence-sourced entitlement, as the portal states it in the token. */
export const cloudLicenceClaimSchema = z.object({
    id: z.string().min(1),
    /** When the licence ends (ISO 8601); the token's `periodEnd` says the same. */
    until: z.string(),
    /** The portal's note on the licence (who it is for, why), shown to the team's owners. */
    note: z.string().max(500).optional(),
});
/** A licence code as the portal prints it: `mjcl_` and 32 characters, in groups the owner may paste with spaces or dashes. */
export const CLOUD_LICENCE_CODE_PREFIX = "mjcl_";
export const cloudLicenceCodeSchema = z
    .string()
    .trim()
    .transform((s) => s.replace(/[\s-]+/g, "").toLowerCase())
    .pipe(z.string().regex(/^mjcl_[a-z0-9]{32}$/, "That does not look like a Mojo Up licence code."));
/** `POST /cloud/v1/organisations/:id/billing/redeem`: the code the owner typed. Answers the new CloudBilling. */
export const billingRedeemSchema = z.object({ code: cloudLicenceCodeSchema });
/** Why a code could not be redeemed, as the portal (or Cloud, before asking it) answers. Each comes with a sentence. */
export const LICENCE_REDEEM_ERRORS = ["licence_unknown", "licence_used", "licence_revoked", "licence_expired", "licence_conflict"];
/** The claims of the portal's signed entitlement token (`saa1.<payload>.<signature>`). Dates are ISO 8601. */
export const cloudEntitlementClaimsSchema = z.object({
    kid: z.string().optional(),
    prd: z.literal(CLOUD_PRODUCT_SLUG),
    /** The portal organisation linked to the Cloud tenant. */
    org: z.string().min(1),
    /** The Cloud tenant (organisation) id. */
    tnt: z.string().min(1),
    plan: cloudPlanSchema,
    sts: cloudSubscriptionStatusSchema,
    seats: z.number().int().min(0),
    trialEnd: z.string().optional(),
    periodEnd: z.string().optional(),
    cancelAtPeriodEnd: z.boolean().optional(),
    /** Where the entitlement comes from: a Stripe subscription (absent reads as that) or a redeemed licence. */
    src: cloudEntitlementSourceSchema.optional(),
    /** The redeemed licence behind a `src: "licence"` token (`lic` means the licence-id string in every other saa1 payload). */
    cl: cloudLicenceClaimSchema.optional(),
    /** Whether the team has a Stripe customer (so Manage billing has somewhere to go), whatever the source. Absent on older tokens: inferred from the status. */
    cus: z.boolean().optional(),
    /** Monotonic per tenant: a token never replaces one with a higher version. */
    ver: z.number().int().min(0),
    iat: z.string(),
    exp: z.string(),
});
/** `POST /internal/v1/entitlements/changed`, from the portal: ids only, signed with the shared key. */
export const entitlementChangedSchema = z.object({
    tenantId: z.string().min(1).max(100),
    version: z.number().int().min(0),
});
export const PORTAL_TIMESTAMP_HEADER = "x-mojo-timestamp";
export const PORTAL_SIGNATURE_HEADER = "x-mojo-signature";
/** How far a push's timestamp may be from Cloud's clock. */
export const PORTAL_PUSH_TOLERANCE_SECONDS = 300;
// ── Cloud -> portal (server to server) ────────────────────────────────────────
export const portalCheckoutRequestSchema = z.object({
    tenantId: z.string().min(1).max(100),
    tenantName: z.string().min(1).max(200),
    ownerEmail: z.string().email(),
    ownerName: z.string().max(200).optional(),
    seats: z.number().int().min(1).max(CLOUD_MAX_SEATS),
    trial: z.boolean(),
    successUrl: z.string().url(),
    cancelUrl: z.string().url(),
});
export const billingPortalFlowSchema = z.enum(["payment_method_update", "subscription_cancel", "subscription_update"]);
export const portalSessionRequestSchema = z.object({
    tenantId: z.string().min(1).max(100),
    returnUrl: z.string().url(),
    flow: billingPortalFlowSchema.optional(),
});
export const portalSeatsRequestSchema = z.object({
    tenantId: z.string().min(1).max(100),
    seats: z.number().int().min(1).max(CLOUD_MAX_SEATS),
    members: z.number().int().min(0),
    preview: z.boolean().optional(),
});
export const prorationPreviewSchema = z.object({ amountDue: z.number().int(), currency: z.string() });
export const portalSeatsResponseSchema = z.object({
    seats: z.number().int(),
    requested: z.number().int().optional(),
    effective: z.literal("now").optional(),
    prorationPreview: prorationPreviewSchema.optional(),
});
export const portalUrlResponseSchema = z.object({ url: z.string().url() });
export const portalEntitlementResponseSchema = z.object({ token: z.string().min(1) });
/**
 * `POST /api/integrations/cloud/licences/redeem`: binds the code to the team (once; the same team
 * may ask again and get the token again) and answers the signed entitlement it now has.
 */
export const portalRedeemRequestSchema = z.object({
    code: cloudLicenceCodeSchema,
    tenantId: z.string().min(1).max(100),
    tenantName: z.string().min(1).max(200),
    ownerEmail: z.string().email(),
    ownerName: z.string().max(200).optional(),
});
// ── Console -> Cloud ──────────────────────────────────────────────────────────
/** `GET /cloud/v1/organisations/:id/billing`: what the Team page's Billing section shows. */
export const cloudBillingSchema = z.object({
    /** The plan the team has now, after the status rules and the offline grace. */
    plan: cloudPlanSchema,
    status: cloudSubscriptionStatusSchema,
    /** Seats bought; 0 on the free plan. */
    seatsPurchased: z.number().int(),
    /** How many people the team may hold now: the seats on Teams, `CLOUD_FREE_TEAM_SEATS` on free. */
    seatLimit: z.number().int(),
    members: z.number().int(),
    openInvitations: z.number().int(),
    trialEnd: z.string().optional(),
    trialDaysLeft: z.number().int().optional(),
    periodEnd: z.string().optional(),
    cancelAtPeriodEnd: z.boolean(),
    /** Where the plan comes from; `none` on the free plan with nothing behind it. */
    source: z.enum(["subscription", "licence", "none"]),
    /** The licence the team runs on (or ran on, when `source` is licence and the plan is free). */
    licence: z.object({ id: z.string(), until: z.string(), seats: z.number().int(), note: z.string().optional(), ended: z.boolean() }).optional(),
    /** A free trial is offered once per team. */
    canStartTrial: z.boolean(),
    /** An owner may redeem a licence code: billing is configured and no paying subscription is running. */
    canRedeem: z.boolean(),
    /** A subscription exists in Stripe (even paused or cancelled), so "Manage billing" has somewhere to go. */
    hasBillingAccount: z.boolean(),
    /** A sentence to show above everything when something needs the owner (a failed payment, a paused trial). */
    banner: z.object({ tone: z.enum(["warning", "danger"]), text: z.string() }).optional(),
    /** False when this deployment has no billing configured: the section explains rather than offers buttons. */
    configured: z.boolean(),
    priceText: z.string(),
    /** When Cloud last heard from the portal; absent when it never has. */
    refreshedAt: z.string().optional(),
    /** The portal could not be reached and Cloud is honouring its last answer. */
    stale: z.boolean(),
});
/** One of the team's owners, as the Billing page's details show them. */
export const billingPersonSchema = z.object({
    userId: z.string(),
    name: z.string().optional(),
    email: z.string().optional(),
});
/**
 * `GET /cloud/v1/organisations/:id/billing/details`: the facts the Billing page's details tab shows
 * beside the plan, from what Cloud itself records (the team, its owners and its audit trail). The
 * card on file, the tax details and the invoices stay with Stripe, through the customer portal.
 */
export const cloudBillingDetailsSchema = z.object({
    organisationId: z.string(),
    organisationName: z.string(),
    /** When the team was made. */
    createdAt: z.string(),
    /** When the team moved to its present plan (its audit trail); absent when it has been on it since it was made. */
    planSince: z.string().optional(),
    owners: z.array(billingPersonSchema),
    /**
     * Who first started a Teams subscription through Checkout, and when: the portal made the Stripe
     * customer with their address, so receipts and tax invoices go there until it is changed in Manage billing.
     */
    billingContact: billingPersonSchema.extend({ since: z.string() }).optional(),
});
export const billingCheckoutSchema = z.object({ seats: z.number().int().min(1).max(CLOUD_MAX_SEATS) });
export const billingPortalSchema = z.object({ flow: billingPortalFlowSchema.optional() });
export const billingSeatsSchema = z.object({ seats: z.number().int().min(1).max(CLOUD_MAX_SEATS), preview: z.boolean().optional() });
/** Where to send the browser next: Stripe Checkout or the Stripe customer portal. */
export const billingRedirectSchema = z.object({ url: z.string().url() });
/** `POST …/billing/seats`: the new billing view, or with `preview` what adding seats would charge now. */
export const billingSeatsResultSchema = z.object({
    billing: cloudBillingSchema,
    prorationPreview: prorationPreviewSchema.optional(),
});
//# sourceMappingURL=billing.js.map